import React, { useCallback, useState, useMemo, useEffect } from "react";
import Table from "../../../../../common/element/Table";
import Button from "../../../../../common/element/Buttons";
import { useNavigate } from "react-router-dom";
import { debounce } from "../../../../../common/snippet/Debouncing";
import ClientInput from "../../../../../common/element/Input/clientInput";
import ModernPagination from "../../../../../common/pagination/ModernPagination";
import { Pagination } from "../../../../../common/pagination";
import { useDispatch, useSelector } from "react-redux";
import { getCoachCompanies } from "../../../../../redux/Coach/coachSlice";
import {
  selectCoachCompanies,
  selectCoachCompaniesLoading,
  selectCoachCompaniesTotal,
} from "../../../../../redux/Coach/coachSelector";
import moment from "moment";
import ThreeDotBouncingLoader from "../../../../../common/element/ButtonLoader/ThreeDotBouncingLoder";
import CommonIconButton from "../../../../../common/icons/CommonIconButton";

const { Tr, Th, Td, THead, TBody } = Table;

const CompaniesList = (props) => {
  const [countData, setCountData] = useState(0);
  const [filterpage, setfilterpage] = useState(1);
  const [filterlimit, setfilterlimit] = useState(10);
  const [totalPages, setTotalPages] = useState(0);
  const navigate = useNavigate();

  const [getFilterData, SetFilterData] = useState({
    function_assignment_id: "",
    page: 1,
    limit: 10,
    sort: "created_date",
    order: "desc",
    search: "",
  });

  const dispatch = useDispatch();
  const coachCompanies = useSelector(selectCoachCompanies);
  const companiesLoading = useSelector(selectCoachCompaniesLoading);
  const companiesTotal = useSelector(selectCoachCompaniesTotal);

  // Read coachId from query string
  const query = new URLSearchParams(window.location.search);
  const coachId = query.get("id");

  const handleSearch = (e) => {
    const value = e.target.value.trim();
    SetFilterData((prev) => ({ ...prev, search: value, page: 1 }));
  };

  const inputchangehandler = useCallback(debounce(handleSearch), []);

  const handlePageChange = (page) => {
    setfilterpage(page);
    SetFilterData((prev) => ({ ...prev, page }));
  };

  // Sync pagination counts from redux selector
  useEffect(() => {
    const total = companiesTotal || 0;
    setCountData(total);
    const limit = getFilterData.limit || filterlimit || 10;
    const pages = limit ? Math.ceil(total / limit) : 0;
    setTotalPages(pages);
    
    // keep local page/limit in sync with filter data
    setfilterpage(getFilterData.page || 1);
    setfilterlimit(getFilterData.limit || limit);
  }, [companiesTotal, getFilterData.page, getFilterData.limit]);

  // ✅ Data to render: use redux data only
  const renderedData = useMemo(() => {
    if (companiesLoading) return []; 
    // If API data exists, return it, otherwise return empty array
    return coachCompanies || [];
  }, [companiesLoading, coachCompanies]);

  // Fetch companies when coachId or filters change
  useEffect(() => {
    if (!coachId) return;
    const page = getFilterData.page || 1;
    const limit = getFilterData.limit || 10;
    const search = getFilterData.search || "";
    dispatch(getCoachCompanies({ coachId, page, limit, search }));
  }, [dispatch, coachId, getFilterData.page, getFilterData.limit, getFilterData.search]);

  return (
    <>
      <div className="flex justify-start mb-4">
        <h1 className="font-lato font-semibold text-[30px] leading-[38px] tracking-[0]">
          Companies
        </h1>
      </div>

      <div className="flex justify-end mb-4">
        <div className="input-wrapper relative">
          <div className="absolute left-3.5 top-2.5">
            <img src="/assets/icons-svg/search-lg.svg" alt="" className="h-5" />
          </div>
          <ClientInput
            type="text"
            className="pl-10 w-80 h-10 text-sm border border-gray-300 rounded-lg text-gray-700 focus:outline-none focus:shadow-outline"
            placeholder="Companies"
            inputchangehandler={inputchangehandler}
            name={"search"}
          />
        </div>
      </div>

      <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
        <Table className="w-full text-sm text-gray-700">
          <THead>
            <Tr className="bg-[#F9F9FB] text-gray-600 text-xs">
              <Th className="py-3 px-4 text-left font-medium">S No.</Th>
              <Th className="py-3 px-4 text-left font-medium">Company Name</Th>
              <Th className="py-3 px-4 text-left font-medium">Contact Person</Th>
              <Th className="py-3 px-4 text-left font-medium">Email Address</Th>
              <Th className="py-3 px-4 text-left font-medium">Mobile Number</Th>
              <Th className="py-3 px-4 text-left font-medium">
                Last Training Session
              </Th>
              <Th className="py-3 px-4 text-center font-medium">Action</Th>
            </Tr>
          </THead>

          <TBody>
            {companiesLoading ? (
              <Tr>
                <Td colSpan={7} className="text-center text-gray-500 py-6 text-sm">
                <ThreeDotBouncingLoader/>
                </Td>
              </Tr>
            ) : renderedData.length > 0 ? (
              renderedData.map((item, index) => (
                <Tr
                  key={item.id || index}
                  className="border-t border-gray-100 hover:bg-gray-50 transition-colors text-start"
                >
                  <Td className="py-3 px-4">
                    {/* Calculate serial number based on page */}
                    {String((getFilterData.page - 1) * getFilterData.limit + index + 1).padStart(2, "0")}
                  </Td>
                  <Td className="py-3 px-4">{item.company_name || "—"}</Td>
                  <Td className="py-3 px-4">{item.contact_person || "—"}</Td>
                  <Td className="py-3 px-4">{item.hr_user?.email || item.contact_email || "—"}</Td>
                  <Td className="py-3 px-4">{item.hr_user?.mobile_number || item.work_phone || "—"}</Td>
                  <Td className="py-3 px-4">
                    {item.last_training_session
                      ? moment(item.last_training_session).format("LL")
                      : "—"}
                  </Td>
                  <Td className="py-3 px-4 text-center">
                    <CommonIconButton
                                    type="view"
                      onClick={() =>
                        navigate(`/companies/companyview?companyId=${item.company_id}`)
                      }
                    />
                     
                  </Td>
                </Tr>
              ))
            ) : (
              <Tr>
                <Td
                  colSpan={7}
                  className="text-center text-gray-500 py-6 text-sm"
                >
                  No matching records found
                </Td>
              </Tr>
            )}
          </TBody>
        </Table>
      </div>

      <div className="flex justify-between items-center mt-3">
        {/* Left side - Pagination info */}
        <Pagination
          totalCount={countData}
          pageNumber={getFilterData.page}
          displayPage={renderedData?.length}
          UpdatePageLimit={(limit) => {
            setfilterlimit(limit);
            SetFilterData((prev) => ({ ...prev, limit, page: 1 }));
          }}
          UpdatePage={(page) => {
            setfilterpage(page);
            SetFilterData((prev) => ({ ...prev, page }));
          }}
        />

        {/* Right side - Modern Pagination Controls */}
        {countData > 0 && totalPages > 1 && (
          <ModernPagination
            totalPages={totalPages}
            currentPage={getFilterData.page}
            onPageChange={(page) => {
              handlePageChange(page);
            }}
          />
        )}
      </div>
    </>
  );
};

export default CompaniesList;